import { v4 as uuidv4 } from "uuid";
import { writable } from "svelte/store";

export type AlertType = "success" | "error" | "warning" | "information" | "progress";
export type AlertLink = { text: string; url: string };

export type AlertOptions = Pick<AlertPayload, "description" | "link" | "maxWidthClass">;

export interface AlertPayload {
  type: AlertType;
  title: string;
  description?: string;
  link?: AlertLink;
  persistent?: boolean;
  progress?: number;
  maxWidthClass?: string;
}

interface Alert extends AlertPayload {
  id: string;
}

const alertStore = () => {
  const { subscribe, update } = writable<Alert[]>([]);

  return {
    subscribe,
    /** Adds an alert to be shown.
     * Returns functions to update and remove the alert.
     */
    add: function (alert: AlertPayload) {
      const id = uuidv4();
      update((state) => [...state, { ...alert, id }]);
      return {
        id,
        /** Remove the alert from display. */
        remove: () => this.remove(id),
        /** Update the alerts content in place, if still visible. */
        update: (alert: AlertPayload) => this.update(id, alert),
      };
    },
    update: (id: string, alert: AlertPayload) => {
      update((state) => state.map((a) => (a.id === id ? { ...alert, id } : a)));
    },
    remove: (id: string) => {
      update((state) => state.filter((a) => a.id !== id));
    },
    success: function (title: string, options?: AlertOptions) {
      return this.add({
        type: "success",
        title,
        description: options?.description,
        link: options?.link,
        maxWidthClass: options?.maxWidthClass,
      });
    },
    error: function (title: string, options?: AlertOptions) {
      return this.add({
        type: "error",
        title,
        description: options?.description,
        link: options?.link,
        maxWidthClass: options?.maxWidthClass,
      });
    },
    warning: function (title: string, options?: AlertOptions) {
      return this.add({
        type: "warning",
        title,
        description: options?.description,
        link: options?.link,
        maxWidthClass: options?.maxWidthClass,
      });
    },
    information: function (title: string, options?: AlertOptions) {
      return this.add({
        type: "information",
        title,
        description: options?.description,
        link: options?.link,
        maxWidthClass: options?.maxWidthClass,
      });
    },
    progress: function (title: string, progress: number, options?: AlertOptions) {
      return this.add({
        type: "progress",
        title,
        persistent: true,
        progress,
        description: options?.description,
        link: options?.link,
        maxWidthClass: options?.maxWidthClass,
      });
    },
  };
};

export const alerts = alertStore();
